import { Link, graphql, navigate } from 'gatsby';
import React, { useContext, useEffect, useMemo } from 'react';

import { isEmpty } from 'lodash';
import { RichText } from 'prismic-reactjs';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { Button, Stack, ListItemButton, ButtonGroup, Popover } from '@mui/material';
import ArrowLeft from '@mui/icons-material/ArrowBackIos';
import Client from 'shopify-buy/index.unoptimized.umd';
import Prismic from '@prismicio/client';

import TimeLineLayout from '../TimeLineLayout';
import { Breadcrumbs, PeopleTimeline } from './components';
import ReadLists from './components/read-lists';
import WatchLists from './components/watch-lists';
import ListenLists from './components/listen-lists';
import { AppContext } from '../../../../apollo/wrap-root-element';
import RelatedList from './components/related-lists';
import { getYear } from '../../../../utils/functions';
import IconShare from '../../../../images/icon-share.svg';
import SocialShare from '../../../../components/Shop/SocialShare';

const Container = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  marginTop: '66px',
  padding: '0 5vw',

  [theme.breakpoints.down('sm')]: {
    marginTop: '4px',
  },
}));

const PeopleName = styled('h1')({
  color: '#fff',
  fontSize: '24px',
  lineHeight: '31px',
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
});

const PeopleLifespan = styled('p')({
  color: '#fff',
  opacity: '0.6',
  fontSize: '18px',
  lineHeight: '22px',
  fontWeight: 500,
});

const StyledButton = styled(ListItemButton)({
  marginRight: 10,
  color: '#0A0A0A',
  backgroundColor: '#F4F5F7',
  padding: '2px 6px',
  borderRadius: 0,
  fontSize: '10px',
  lineHeight: '18px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',

  flex: 'none',
  order: 0,
  flexGrow: 0,

  '&:hover': {
    backgroundColor: '#FECB04',
  },
});

const BackStyledButton = styled(Button)(({ theme }) => ({
  // marginRight: 4,
  color: '#F4F5F7',
  background: 'rgba(255, 255, 255, 0.06)',
  padding: '8px 10px 8px 10px',
  borderRadius: 0,
  opacity: 0.5,
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: 500,
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
  width: '50%',

  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const PeopleImage = styled('img')({
  minWidth: 280,
  maxWidth: '100%',
  maxHeight: '375px',
});

const PeopleDescription = styled('div')({
  '& p': {
    color: '#fff',
    opacity: '0.8',
    fontSize: '16px',
    lineHeight: '24px',
  },
});

const SubHeading = styled('h2')({
  fontWeight: 600,
  fontSize: '19px',
  lineHeight: '24px',
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
  color: '#fff',
});

const HeadBox = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    gap: '20px',
    marginBottom: '30px',
  },
}));

const shopifyClient = Client.buildClient({
  storefrontAccessToken: process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN,
  domain: process.env.GATSBY_SHOPIFY_STORE_URL,
});

const People = (props) => {
  const {
    pageContext: { peopleBirthDate, peopleDeathDate, peopleImage, profilePicture, peopleName, peopleSummary },
    data: { allReads, totalWatchData, totalShopData, totalEventData, relatedListen },
    location: { href },
  } = props;

  const { activeTabForPeopleSpecificPage, setActiveTabForPeopleSpecificPage } = useContext(AppContext);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [podCastData, setPodCastData] = React.useState([]);
  const [productLists, setProductLists] = React.useState([]);

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeShare = () => {
    setAnchorEl(null);
  };

  const openShare = Boolean(anchorEl);

  const seoData = {
    canonical: href || '',
    title: peopleName || '',
    metaDesc: peopleSummary[0]?.text || `Read more about ${peopleName}`,
    opengraphDescription: peopleSummary[0]?.text || `Read more about ${peopleName}`,
    twitterDescription: peopleSummary[0]?.text || `Read more about ${peopleName}`,
    opengraphTitle: peopleName || '',
    twitterTitle: peopleName || '',
    opengraphImage: {
      sourceUrl: profilePicture || peopleImage || '',
    },
    twitterImage: {
      sourceUrl: profilePicture || peopleImage || '',
    },
    opengraphUrl: href || '',
  };

  const apiEndpoint = 'https://lineage.prismic.io/api/v2';
  const client = Prismic.client(apiEndpoint);

  const queryFromPrismic = async (keyword, fields, orderings, saveData) => {
    await client
      .query([Prismic.Predicates.any('document.type', fields), Prismic.Predicates.fulltext('document', `${keyword}`)], {
        pageSize: 100,
        ...orderings,
      })
      .then((response) => {
        if (response.results.length > 0) {
          saveData(response.results);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          saveData([]);
        }
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const fetchSearchResult = async (keyword) => {
    if (keyword.length < 3) return;

    setIsLoading(true);
    queryFromPrismic(
      keyword,
      ['read', 'episodes'],
      {
        orderings: '[my.read.title, my.read.priority]',
        orderings: '[my.episodes.title, my.episodes.priority]',
      },
      setData
    );

    queryFromPrismic(
      keyword,
      ['podcast'],
      {
        orderings: '[my.podcast.title, my.podcast.priority]',
      },
      setPodCastData
    );
  };

  const readData = useMemo(() => {
    const formattedData = data
      .filter((r) => r.type === 'read')
      ?.map((read) => ({
        data: {
          featuredImage: read.data.featured_image,
          priority: read.data.priority,
          readDuration: { text: read.data.read_duration1[0]?.text },
          title: { text: read.data.title[0]?.text },
        },
        id: read?.id,
        url: `/read/${read.uid}`,
      }));

    return { nodes: formattedData };
  }, [data]);

  const watchData = useMemo(() => {
    const formattedData = data
      .filter((e) => e.type === 'episodes' && e.data.language === 'English')
      ?.map((watch) => ({
        data: {
          thumbnailImage: { url: watch.data.thumbnail_image?.url },
          priority: watch.data.priority,
          videoDuration: [{ text: watch.data.video_duration[0]?.text }],
          title: { text: watch.data.title[0]?.text },
          youtubeId: [{ text: watch.data.youtube_id[0]?.text }],
          bts: watch.bts,
        },
        uid: watch?.uid,
        url: `/video_episodes/${watch?.uid}`,
      }));

    return { nodes: formattedData };
  }, [data]);

  const listenData = useMemo(() => {
    const formattedData = podCastData?.map((podcast) => ({
      data: {
        thumbnailImage: { url: podcast.data.thumbnail_image?.url },
        podCastTitle: [{ text: podcast.data.podcast_title[0]?.text }],
        podcastDuration: podcast.data.podcast_duration,
      },
      uid: podcast?.uid,
      url: `/podcast/${podcast.uid}`,
    }));

    return { nodes: formattedData };
  }, [podCastData]);

  useEffect(() => {
    if (peopleName !== '') {
      fetchSearchResult(peopleName);
    }
  }, [peopleName]);

  return (
    <TimeLineLayout seo={seoData}>
      <Container>
        <Stack direction="column">
          <Stack direction="column">
            <HeadBox>
              <Breadcrumbs {...props} />
              <ButtonGroup sx={{ width: { xs: '100%', sm: 'auto' }, gap: '4px' }}>
                <BackStyledButton variant="contained" startIcon={<ArrowLeft />} onClick={() => navigate(-1)}>
                  back
                </BackStyledButton>
                <BackStyledButton
                  variant="contained"
                  startIcon={<img src={IconShare} alt="IconShare" style={{ marginRight: '5px' }} />}
                  onClick={handleShareClick}
                >
                  share
                </BackStyledButton>
                <Popover
                  id="share-popover"
                  open={openShare}
                  anchorEl={anchorEl}
                  onClose={closeShare}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <SocialShare title={peopleName} className={'social-share__light'} />
                </Popover>
              </ButtonGroup>
            </HeadBox>
          </Stack>
          <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ mr: { xs: '0', sm: '32px' } }}>
              <PeopleImage src={profilePicture ?? peopleImage} alt={peopleName} />
            </Box>
            <Box sx={{ maxWidth: '649px', mt: { xs: '20px', sm: '0' } }}>
              <PeopleName>{peopleName}</PeopleName>
              <PeopleLifespan>
                {getYear(peopleBirthDate)}–{peopleDeathDate ? getYear(peopleDeathDate) : 'Present'}
              </PeopleLifespan>
              <Stack direction="row" sx={{ mt: '15px', mb: '19px' }}>
                {!isEmpty(readData.nodes) && (
                  <StyledButton
                    variant="contained"
                    onClick={() => setActiveTabForPeopleSpecificPage('read')}
                    selected={activeTabForPeopleSpecificPage === 'read'}
                  >
                    read
                  </StyledButton>
                )}
                {!isEmpty(watchData.nodes) && (
                  <StyledButton
                    variant="contained"
                    onClick={() => setActiveTabForPeopleSpecificPage('watch')}
                    selected={activeTabForPeopleSpecificPage === 'watch'}
                  >
                    watch
                  </StyledButton>
                )}
                {listenData.nodes.length && (
                  <StyledButton
                    variant="contained"
                    onClick={() => setActiveTabForPeopleSpecificPage('listen')}
                    selected={activeTabForPeopleSpecificPage === 'listen'}
                  >
                    listen
                  </StyledButton>
                )}
                {!isEmpty(totalShopData.nodes[0].data.related_products[0].product_id) && (
                  <StyledButton
                    variant="contained"
                    onClick={() => setActiveTabForPeopleSpecificPage('related')}
                    selected={activeTabForPeopleSpecificPage === 'related'}
                  >
                    related
                  </StyledButton>
                )}
              </Stack>
              <PeopleDescription>{RichText.render(peopleSummary)}</PeopleDescription>
            </Box>
          </Stack>
        </Stack>
        <Box sx={{ mt: '40px' }}>
          {activeTabForPeopleSpecificPage === 'timeline' && (
            <>
              <SubHeading>Timeline</SubHeading>
              <PeopleTimeline events={totalEventData} />
            </>
          )}
        </Box>

        <Box sx={{ mt: '40px' }}>
          {activeTabForPeopleSpecificPage !== 'timeline' && (
            <Box className="pd-x-sm">
              <BackStyledButton
                variant="contained"
                startIcon={<ArrowLeft />}
                onClick={() => setActiveTabForPeopleSpecificPage('timeline')}
                sx={{ width: 'auto' }}
              >
                Back to Timeline
              </BackStyledButton>
            </Box>
          )}

          {activeTabForPeopleSpecificPage === 'read' && <ReadLists allReads={readData} />}
          {activeTabForPeopleSpecificPage === 'listen' && <ListenLists allListen={listenData} />}
          {activeTabForPeopleSpecificPage === 'watch' && <WatchLists totalWatchData={watchData} />}
          {activeTabForPeopleSpecificPage === 'related' && <RelatedList totalShopData={totalShopData} />}
        </Box>
      </Container>
    </TimeLineLayout>
  );
};

export const query = graphql`
  query TimelineReadAndWatch($uid: String) {
    allReads: allPrismicRead(
      sort: { fields: data___priority, order: ASC }
      filter: { data: { related_people: { elemMatch: { related_person: { uid: { eq: $uid } } } } } }
    ) {
      nodes {
        data {
          priority
          title {
            text
          }
          featuredImage: featured_image {
            alt
            url
            fluid(maxWidth: 600) {
              ...GatsbyPrismicImageFluid
            }
          }
          readDuration: read_duration1 {
            text
          }
        }
        url
        id
      }
    }
    relatedListen: allPrismicPodcast(
      filter: { data: { related_people: { elemMatch: { related_person: { uid: { eq: $uid } } } } } }
    ) {
      nodes {
        uid
        data {
          podcastDuration: podcast_duration
          thumnailImage: thumbnail_image {
            url
          }
          podCastTitle: podcast_title {
            text
          }
        }
      }
    }
    totalWatchData: allPrismicEpisodes(
      filter: { data: { people: { elemMatch: { people1: { uid: { eq: $uid } } } }, language: { eq: "English" } } }
      sort: { fields: data___priority, order: ASC }
    ) {
      nodes {
        data {
          priority
          videoDuration: video_duration {
            text
          }
          youtubeId: youtube_id {
            text
          }
          bts
          title {
            text
          }
          thumbnailImage: thumbnail_image {
            url
          }
        }
        uid
        url
      }
    }

    totalShopData: allPrismicPeople(filter: { uid: { eq: $uid } }) {
      nodes {
        data {
          related_products {
            product_id
          }
        }
      }
    }

    totalEventData: allPrismicEvents(
      sort: { fields: data___event_year, order: ASC }
      filter: { data: { related_people: { elemMatch: { related_person: { uid: { eq: $uid } } } } } }
    ) {
      nodes {
        uid
        data {
          event_end_date
          event_year
          date_accuracy
          date_type
          featured_image {
            url
          }
          title {
            text
          }
        }
      }
    }
  }
`;

export default People;
